import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import {
  defaultStyles as tooltipDefaultStyles,
  useTooltip,
  useTooltipInPortal,
} from '@visx/tooltip';
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { Icon } from 'js/components';
import OrgTreeNodeTypeIcon from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/OrgTreeNodeTypeIcon';
import { extractTemplateQuestionSets } from 'js/utils/org-assessment-order';
import { getCategoryLookups } from 'js/utils/question-set';
import { buildOrgTrendsCategoryLookups } from 'js/utils/org-trends';
import { inkColorFor, scaleColorTraffic, toHexColor } from 'js/utils/color';
import { eachMark } from './briefing-marks';

import styles from './BriefingHeatmap.module.scss';

// A condensed heatmap of every team on every question set, built from the
// data the server stores on the briefing (OrgBriefingHeatmap), so it always
// matches the text beside it.
//
// Columns are every question set and its questions, with no fold. Both use
// a light wash of their category color. Sets stay bold and questions stay
// italic. Rows open on the teams the briefing cites. Columns that do not
// fit scroll, the same way the squad scorecard does.

const MAX_OPEN_GROUPS = 4;

// The box is portaled onto the page, so it has to clear the heatmap's own
// layers (cited rings, sticky names, column headers) and the app sidebar.
const TOOLTIP_STYLE = {
  ...tooltipDefaultStyles,
  lineHeight: 1.35,
  maxWidth: '18rem',
  zIndex: 2000,
};

function questionKey(setKey, key) {
  return `${setKey}|${key}`;
}

// The mean of several [latest, delta, dir] cells, colored only when most of
// them lean the same way.
export function aggregateCells(cells) {
  const present = cells.filter((cell) => cell != null && cell[0] != null);
  if (present.length === 0) {
    return null;
  }
  const mean = (index) => {
    const values = present.map((c) => c[index]).filter((v) => v != null);
    return values.length
      ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) /
          10
      : null;
  };
  const delta = mean(1);
  let dir = null;
  if (delta != null && Math.abs(delta) >= MOVE_QUIET_UNDER) {
    dir = delta > 0 ? 'up' : 'down';
  }
  return [mean(0), delta, dir];
}

// Every team, measure and set name the briefing cites, from its marks.
export function briefingCitations(document) {
  const cited = { teams: new Set(), measures: new Set(), sets: new Set() };
  const scan = (text) => {
    eachMark(text, (match) => {
      if (match[1] === 'team' || match[1] === 'measure' || match[1] === 'set') {
        cited[`${match[1]}s`].add(match[2]);
      }
    });
  };
  scan(document?.intro);
  for (const section of document?.sections ?? []) {
    for (const item of section.items ?? []) {
      scan(item.body);
    }
  }
  return cited;
}

function itemCitations(item) {
  return briefingCitations({ sections: [{ items: item ? [item] : [] }] });
}

// Which sets and questions a set of citations points at, by key.
function resolveCitations(cited, heatmap) {
  const setKeys = new Set();
  const questionKeys = new Set();
  for (const set of heatmap.sets) {
    if (cited.sets.has(set.name)) setKeys.add(set.key);
    for (const q of set.questions) {
      if (cited.measures.has(q.label)) {
        questionKeys.add(questionKey(set.key, q.key));
      }
    }
  }
  for (const category of heatmap.categories ?? []) {
    if (cited.sets.has(category.name)) {
      category.sets.forEach((key) => setKeys.add(key));
    }
  }
  const teamIds = new Set(
    heatmap.teams.filter((t) => cited.teams.has(t.name)).map((t) => t.id)
  );
  return { setKeys, questionKeys, teamIds };
}

function initialOpen(heatmap, resolved) {
  const groups = (heatmap.groups ?? []).filter((g) =>
    g.team_ids.some((id) => resolved.teamIds.has(id))
  );
  return {
    groups: new Set(
      groups.length <= MAX_OPEN_GROUPS ? groups.map((g) => g.id) : []
    ),
  };
}

// Category colors match the radial and radar charts: the assessment or rubric
// config assigns palette-cat-N by ordinal. Heatmap category names are the
// catalog keys, except the catch-all, which the server calls "Uncategorized".
function categoryKey(name) {
  if (!name || name === 'Uncategorized') return 'uncategorized';
  return name;
}

export function briefingCategoryLookups({
  isRubric = false,
  assessmentTemplate = null,
  heatmap = null,
} = {}) {
  const heatmapByKey = new Map(
    (heatmap?.sets ?? []).filter((set) => set?.key).map((set) => [set.key, set])
  );
  const fromTemplate = extractTemplateQuestionSets(assessmentTemplate).map(
    (qs) => ({
      key: qs.key,
      category: categoryKey(qs.category || heatmapByKey.get(qs.key)?.category),
    })
  );
  const seen = new Set(fromTemplate.map((qs) => qs.key));
  const questionSets = [
    ...fromTemplate,
    ...[...heatmapByKey.values()]
      .filter((set) => !seen.has(set.key))
      .map((set) => ({
        key: set.key,
        category: categoryKey(set.category),
      })),
  ];
  const hasConfig = Boolean(
    assessmentTemplate?.config?.categories ||
      assessmentTemplate?.assessment?.config?.categories
  );

  if (isRubric || hasConfig) {
    return buildOrgTrendsCategoryLookups({
      isRubric,
      assessmentTemplate,
      questionSets,
    });
  }

  const supplemental = {};
  let ordinal = 1;
  for (const set of questionSets) {
    if (!set.category || supplemental[set.category]) continue;
    supplemental[set.category] = { ordinal: ordinal++ };
  }
  return getCategoryLookups([], supplemental);
}

function resolveCategory(name, lookups) {
  if (!name || !lookups?.size) return null;
  const direct = lookups.get(name) || lookups.get(categoryKey(name));
  if (direct) return direct;
  const lower = name.toLowerCase();
  for (const [key, value] of lookups) {
    if (key.toLowerCase() === lower) return value;
    if (value?.display_name?.toLowerCase() === lower) return value;
  }
  return null;
}

function rowIcon(row) {
  if (row.type === 'team') return { type: 'team' };
  if (row.type === 'org') return { type: 'org', isRoot: true };
  return { type: 'org', isRoot: false };
}

const ROW_PALETTE = {
  org: 'palette-tiers-1',
  group: 'palette-tiers-2',
};

function rowPaint(row, wash) {
  const palette = ROW_PALETTE[row.type];
  return palette ? `${palette} ${wash}` : null;
}

function rowTone(row) {
  return rowPaint(row, 'muted-white-bg');
}

// A see-through film of the row color, so it can sit on the column wash.
function rowFilm(row) {
  return rowPaint(row, 'muted-bg');
}

function FoldIcon({ open }) {
  return (
    <span className={styles.foldIcon} aria-hidden="true">
      <Icon icon={open ? faChevronDown : faChevronRight} fixedWidth />
    </span>
  );
}

function placeColumns(columns) {
  return columns.map((column, index) => ({
    column,
    gridColumn: index + 2,
    categoryStart:
      index > 0 &&
      (column.category ?? null) !== (columns[index - 1].category ?? null),
  }));
}

function questionTextFromTemplate(template) {
  const text = new Map();
  extractTemplateQuestionSets(template).forEach((set) => {
    (set.questions ?? []).forEach((question) => {
      const full = question.text || question.name;
      if (set.key && question.key && full) {
        text.set(questionKey(set.key, question.key), full);
      }
    });
  });
  return text;
}

function buildColumns(heatmap, questionText) {
  const setsByKey = new Map(heatmap.sets.map((s) => [s.key, s]));
  const categories = heatmap.categories ?? [
    { name: null, sets: heatmap.sets.map((s) => s.key) },
  ];
  const columns = [];
  for (const category of categories) {
    for (const key of category.sets) {
      const set = setsByKey.get(key);
      if (!set) continue;
      columns.push({
        type: 'set',
        id: `s:${set.key}`,
        label: set.name,
        category: category.name,
        setKeys: [set.key],
      });
      for (const question of set.questions ?? []) {
        const qKey = questionKey(set.key, question.key);
        const text = question.text || questionText.get(qKey) || null;
        let shortLabel = question.label || null;
        if (
          shortLabel &&
          (shortLabel === text || shortLabel === question.key)
        ) {
          shortLabel = null;
        }
        columns.push({
          type: 'question',
          id: `q:${qKey}`,
          key: question.key,
          label: shortLabel || text || question.key,
          shortLabel,
          text,
          category: category.name,
          setKeys: [set.key],
          questionKey: qKey,
        });
      }
    }
  }
  return columns;
}

function groupTeams(group, teamsById) {
  // A team can sit in several places under one org. The score is the same,
  // so the org lists it once. The same team under a different org is a
  // separate row, keyed with that org, so opening and closing one does not
  // reuse the other's row.
  const seen = new Set();
  const teams = [];
  for (const id of group.team_ids ?? []) {
    if (seen.has(id)) continue;
    const team = teamsById.get(id);
    if (!team) continue;
    seen.add(id);
    teams.push(team);
  }
  return teams;
}

function buildRows(heatmap, open) {
  const teamsById = new Map(heatmap.teams.map((t) => [t.id, t]));
  const rows = [{ type: 'org', id: 'org', label: 'Org average', teams: [] }];
  if (heatmap.groups?.length) {
    for (const group of heatmap.groups) {
      const teams = groupTeams(group, teamsById);
      rows.push({
        type: 'group',
        id: `g:${group.id}`,
        groupId: group.id,
        label: group.name,
        teams,
      });
      if (open.groups.has(group.id)) {
        for (const team of teams) {
          rows.push({
            type: 'team',
            id: `t:${group.id}:${team.id}`,
            label: team.name,
            team,
            teams: [team],
            nested: true,
          });
        }
      }
    }
  } else {
    for (const team of heatmap.teams) {
      rows.push({
        type: 'team',
        id: `t:${team.id}`,
        label: team.name,
        team,
        teams: [team],
      });
    }
  }
  return rows;
}

function teamCell(team, column) {
  if (column.type === 'question') return team.questions?.[column.questionKey];
  return team.sets?.[column.setKeys[0]];
}

function orgCell(heatmap, column) {
  if (column.type === 'question') {
    return heatmap.org.questions?.[column.questionKey];
  }
  return heatmap.org.sets?.[column.setKeys[0]];
}

function scored(cell) {
  if (cell == null || cell[0] == null) return null;
  return cell;
}

function cellFor(heatmap, row, column) {
  if (row.type === 'org') return scored(orgCell(heatmap, column));
  if (row.type === 'team') return scored(teamCell(row.team, column));
  return scored(
    aggregateCells(row.teams.map((team) => teamCell(team, column)))
  );
}

function columnCited(column, resolved) {
  if (column.type === 'question') {
    return resolved.questionKeys.has(column.questionKey);
  }
  return column.setKeys.some(
    (key) =>
      resolved.setKeys.has(key) ||
      [...resolved.questionKeys].some((q) => q.startsWith(`${key}|`))
  );
}

function rowCited(row, resolved) {
  return row.teams.some((team) => resolved.teamIds.has(team.id));
}

// The team x set and team x question pairs the briefing discusses, bullet by
// bullet: a bullet naming Tax API and Forecast Integrity outlines that one
// cell, not every cell in Tax API's row and Forecast Integrity's column. A
// bullet naming no team is about the org, so it marks the org's row.
export function citedPairs(items, heatmap) {
  const pairs = new Set();
  for (const item of items) {
    const resolved = resolveCitations(itemCitations(item), heatmap);
    const rows = resolved.teamIds.size ? [...resolved.teamIds] : ['org'];
    for (const row of rows) {
      resolved.setKeys.forEach((key) => pairs.add(`${row}#s:${key}`));
      resolved.questionKeys.forEach((key) => {
        pairs.add(`${row}#q:${key}`);
        pairs.add(`${row}#s:${key.split('|')[0]}`);
      });
    }
  }
  return pairs;
}

function cellCited(row, column, pairs, open) {
  // An open group's own row repeats its teams' cells; they carry the outline.
  if (row.type === 'group' && open.groups.has(row.groupId)) return false;
  const rowIds = row.type === 'org' ? ['org'] : row.teams.map((t) => t.id);
  const columnKeys =
    column.type === 'question'
      ? [`q:${column.questionKey}`]
      : column.setKeys.map((key) => `s:${key}`);
  return rowIds.some((id) =>
    columnKeys.some((key) => pairs.has(`${id}#${key}`))
  );
}

function documentItems(document) {
  return (document?.sections ?? []).flatMap((section) => section.items ?? []);
}

function formatPoints(value) {
  if (value == null) return '–';
  return `${Math.round(value)}%`;
}

function formatChange(value) {
  if (value == null) return 'no change on record';
  if (Math.abs(value) < 0.5) return 'no change';
  const points = Math.round(Math.abs(value));
  return `${value > 0 ? 'up' : 'down'} ${points} point${
    points === 1 ? '' : 's'
  }`;
}

// Color follows the change the tooltip shows, on one scale for every cell.
// Under 5 points stays gray. Then under 10, 10 to 20, and 20 or more.
const MOVE_QUIET_UNDER = 5;
const MOVE_STEP_AT = [10, 20];

export function movementKey(cell) {
  if (!cell || cell[1] == null) return null;
  const delta = Number(cell[1]);
  if (!Number.isFinite(delta) || Math.abs(delta) < MOVE_QUIET_UNDER)
    return null;
  const dir = delta > 0 ? 'up' : 'down';
  const magnitude = Math.abs(delta);
  const step =
    magnitude >= MOVE_STEP_AT[1] ? 3 : magnitude >= MOVE_STEP_AT[0] ? 2 : 1;
  return `${dir}${step}`;
}

function cellScore(cell) {
  if (!cell) return 'No score';
  return `${formatPoints(cell[0])}, ${formatChange(cell[1])}`;
}

// Scores on the heatmap are 0–100. The traffic scale runs 0–1.
function scorePaint(latest) {
  const value = Number(latest);
  if (!Number.isFinite(value)) return null;
  const fraction = Math.min(1, Math.max(0, value / 100));
  const background = toHexColor(scaleColorTraffic(fraction));
  return {
    background,
    color: inkColorFor(fraction, background),
    label: String(Math.round(value)),
  };
}

function cellTitle(row, column, cell) {
  if (!cell) return `${row.label} · ${column.label}: no score`;
  return `${row.label} · ${column.label}: ${cellScore(cell)}`;
}

function columnTip(column) {
  if (column.type !== 'question') return { title: column.label };
  const title = column.shortLabel || column.key;
  const text = column.text && column.text !== title ? column.text : null;
  return { title, text };
}

function Details({ selection, reads, onClose }) {
  if (selection?.type !== 'team') return null;
  const { team } = selection;
  return (
    <div className={styles.details}>
      <div className={styles.detailsHeader}>
        <strong>{team.name}</strong>
        <button type="button" className={styles.close} onClick={onClose}>
          Close
        </button>
      </div>
      <p className={styles.detailsText}>
        {reads?.get(team.name) || 'No read for this team.'}
      </p>
      {team.responses != null ? (
        <p className={styles.detailsMuted}>
          {team.responses} response{team.responses === 1 ? '' : 's'} in its
          latest cycle
        </p>
      ) : null}
    </div>
  );
}

function HeatmapRows({
  rows,
  placed,
  heatmap,
  pairs,
  focusPairs,
  open,
  lookups,
  focus,
  onTip,
  onHideTip,
  onToggleGroup,
  onSelectTeam,
}) {
  return rows.map((row) => {
    const tone = rowTone(row);
    return (
      <div key={row.id} className={cx(styles.dataRow, tone)}>
        <div className={styles.rowHeaderBack}>
          <button
            type="button"
            className={cx(
              styles.rowHeader,
              tone,
              row.type === 'org' && styles.orgRow,
              row.type === 'group' && styles.groupRow,
              focus && rowCited(row, focus) && styles.focused
            )}
            title={row.label}
            onClick={() => {
              if (row.type === 'group') onToggleGroup(row.groupId);
              if (row.type === 'team') onSelectTeam(row.team);
            }}
          >
            <span
              className={cx(
                styles.rowHeaderBody,
                row.type === 'org' && styles.orgRowBody,
                row.nested && styles.nestedRow
              )}
            >
              {row.type === 'group' ? (
                <FoldIcon open={open.groups.has(row.groupId)} />
              ) : null}
              <span className={styles.rowIcon}>
                <OrgTreeNodeTypeIcon node={rowIcon(row)} />
              </span>
              <span className={styles.rowLabel}>{row.label}</span>
            </span>
          </button>
        </div>
        {placed.map(({ column, gridColumn, categoryStart }) => {
          const cell = cellFor(heatmap, row, column);
          const cited = cellCited(row, column, pairs, open);
          const focused = cellCited(row, column, focusPairs, open);
          const info = resolveCategory(column.category, lookups);
          const label = cellTitle(row, column, cell);
          const film = rowFilm(row);
          const paint = cell ? scorePaint(cell[0]) : null;
          return (
            <div
              key={`${row.id}-${column.id}`}
              className={cx(
                styles.cell,
                styles.categoryCell,
                categoryStart && styles.gapColumn,
                !cell && styles.empty,
                styles[movementKey(cell)],
                (cited || focused) && styles.citedCell,
                focused && styles.focusedCell
              )}
              style={{ gridColumn }}
              aria-label={label}
              onMouseEnter={(event) => {
                onTip(event, {
                  who: row.label,
                  ...columnTip(column),
                  score: cellScore(cell),
                });
              }}
              onMouseLeave={onHideTip}
            >
              <span className={cx(styles.columnColor, info?.palette)} />
              <span
                className={cx(
                  styles.columnWash,
                  column.type === 'question' && styles.questionWash
                )}
              />
              {film ? <span className={cx(styles.rowWash, film)} /> : null}
              {paint ? (
                <span
                  className={styles.scoreMark}
                  aria-hidden="true"
                  style={{
                    background: paint.background,
                    color: paint.color,
                  }}
                >
                  {paint.label}
                </span>
              ) : null}
              {cited || focused ? (
                <span
                  className={cx(
                    styles.citeMark,
                    focused && styles.citeMarkFocused
                  )}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    );
  });
}

const MemoHeatmapRows = React.memo(HeatmapRows);

export default function BriefingHeatmap({
  heatmap,
  document,
  focusItem = null,
  reads = null,
  assessmentTemplate = null,
  isRubric = false,
  categoryLookups = null,
}) {
  const resolved = useMemo(
    () =>
      heatmap ? resolveCitations(briefingCitations(document), heatmap) : null,
    [heatmap, document]
  );
  const focus = useMemo(
    () =>
      heatmap && focusItem
        ? resolveCitations(itemCitations(focusItem), heatmap)
        : null,
    [heatmap, focusItem]
  );
  const pairs = useMemo(
    () => (heatmap ? citedPairs(documentItems(document), heatmap) : new Set()),
    [heatmap, document]
  );
  const focusPairs = useMemo(
    () => (heatmap && focusItem ? citedPairs([focusItem], heatmap) : new Set()),
    [heatmap, focusItem]
  );
  const lookups = useMemo(() => {
    if (categoryLookups?.size) return categoryLookups;
    return heatmap
      ? briefingCategoryLookups({ isRubric, assessmentTemplate, heatmap })
      : new Map();
  }, [categoryLookups, isRubric, assessmentTemplate, heatmap]);
  const [showScores, setShowScores] = useState(false);
  const [open, setOpen] = useState(() =>
    heatmap ? initialOpen(heatmap, resolved) : null
  );
  const [selection, setSelection] = useState(null);
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();
  // The box renders on <body>, positioned from the scroll frame, so the
  // horizontal scroller cannot clip it or shift its origin.
  const { containerRef, containerBounds, TooltipInPortal } = useTooltipInPortal(
    {
      detectBounds: true,
      scroll: true,
    }
  );
  const scrollerRef = useRef(null);
  const nameRef = useRef(null);
  const [scrollMetrics, setScrollMetrics] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    pageScrollWidth: 0,
    nameColumnWidth: 0,
  });

  const questionText = useMemo(
    () => questionTextFromTemplate(assessmentTemplate),
    [assessmentTemplate]
  );
  const columns = useMemo(
    () => (heatmap ? buildColumns(heatmap, questionText) : []),
    [heatmap, questionText]
  );
  const rows = useMemo(
    () => (heatmap && open ? buildRows(heatmap, open) : []),
    [heatmap, open]
  );
  const placed = useMemo(() => placeColumns(columns), [columns]);
  const boundsRef = useRef(containerBounds);
  boundsRef.current = containerBounds;
  const showTipRef = useRef(showTooltip);
  showTipRef.current = showTooltip;
  const hideTipRef = useRef(hideTooltip);
  hideTipRef.current = hideTooltip;
  const onTip = useCallback((event, data) => {
    const bounds = boundsRef.current;
    const rect = event.currentTarget.getBoundingClientRect();
    showTipRef.current({
      tooltipLeft: rect.left - bounds.left + rect.width / 2,
      tooltipTop: rect.top - bounds.top + rect.height / 2,
      tooltipData: data,
    });
  }, []);
  const onHideTip = useCallback(() => {
    hideTipRef.current();
  }, []);
  const onToggleGroup = useCallback((id) => {
    setOpen((current) => {
      const next = new Set(current.groups);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...current, groups: next };
    });
  }, []);
  const onSelectTeam = useCallback((team) => {
    setSelection({ type: 'team', team });
  }, []);

  const updateScrollMetrics = useCallback(() => {
    const scrollEl = scrollerRef.current;
    const nameEl = nameRef.current;
    if (!scrollEl) return;

    const nameColumnWidth = nameEl?.offsetWidth ?? 0;
    const pageScrollWidth = Math.max(0, scrollEl.clientWidth - nameColumnWidth);
    const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
    const hasOverflow = maxScrollLeft > 1;
    const next = {
      canScrollLeft: hasOverflow && scrollEl.scrollLeft > 1,
      canScrollRight: hasOverflow && scrollEl.scrollLeft < maxScrollLeft - 1,
      pageScrollWidth,
      nameColumnWidth,
    };

    setScrollMetrics((prev) => {
      if (
        prev.canScrollLeft === next.canScrollLeft &&
        prev.canScrollRight === next.canScrollRight &&
        prev.pageScrollWidth === next.pageScrollWidth &&
        prev.nameColumnWidth === next.nameColumnWidth
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    updateScrollMetrics();
  }, [updateScrollMetrics, columns.length, rows.length]);

  useEffect(() => {
    const scrollEl = scrollerRef.current;
    if (!scrollEl || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => updateScrollMetrics());
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [updateScrollMetrics, columns.length]);

  const scrollByPage = useCallback(
    (direction) => {
      const scrollEl = scrollerRef.current;
      if (!scrollEl || scrollMetrics.pageScrollWidth <= 0) return;
      const delta =
        direction === 'left'
          ? -scrollMetrics.pageScrollWidth
          : scrollMetrics.pageScrollWidth;
      scrollEl.scrollBy({ left: delta, behavior: 'smooth' });
    },
    [scrollMetrics.pageScrollWidth]
  );

  if (!heatmap || !open || heatmap.teams.length === 0) {
    return null;
  }

  const hasCategories = Boolean(heatmap.categories?.length);
  const legendCategories = (heatmap.categories ?? []).filter((c) => c.name);

  // One track per score column. The name column is the first track. A new
  // category starts with a left margin, so the template does not need a
  // gap column between them.
  const bands = [];
  if (hasCategories) {
    for (const { column, gridColumn } of placed) {
      const last = bands[bands.length - 1];
      if (last && last.category === column.category) last.span += 1;
      else {
        bands.push({
          category: column.category,
          span: 1,
          start: gridColumn,
          column,
          categoryStart: bands.length > 0,
        });
      }
    }
  }

  const gridStyle = {
    '--heatmap-cols': columns.length,
  };
  const frameStyle = {
    '--heatmap-name-width': `${scrollMetrics.nameColumnWidth}px`,
  };

  return (
    // A div, not a <figure>: the briefing styles every figure as a chart card.
    <div
      className={cx(styles.heatmap, showScores && styles.scores)}
      role="figure"
      aria-label="Heatmap of every team on every question set"
      data-test="briefing-heatmap"
      data-copy-exclude=""
    >
      <div className={styles.chartHead}>
        {legendCategories.length > 0 ? (
          <div
            className={styles.categoryLegend}
            data-test="briefing-heatmap-categories"
          >
            {legendCategories.map((category) => {
              const info = resolveCategory(category.name, lookups);
              return (
                <span
                  key={category.name}
                  className={cx('category-pill', info?.palette)}
                >
                  {info?.display_name || category.name}
                </span>
              );
            })}
          </div>
        ) : (
          <div />
        )}
        <div
          className={styles.modeSwitch}
          role="group"
          aria-label="Heatmap view"
        >
          <button
            type="button"
            className={styles.modeButton}
            aria-pressed={!showScores}
            onClick={() => setShowScores(false)}
          >
            Change
          </button>
          <button
            type="button"
            className={styles.modeButton}
            aria-pressed={showScores}
            data-test="heatmap-mode-scores"
            onClick={() => setShowScores(true)}
          >
            Scores
          </button>
        </div>
      </div>
      <div className={styles.scrollFrame} style={frameStyle} ref={containerRef}>
        {scrollMetrics.canScrollLeft ? (
          <button
            type="button"
            className={cx(styles.scrollArrow, styles.scrollArrowLeft)}
            onClick={() => scrollByPage('left')}
            aria-label="Scroll left"
          >
            <Icon icon={faChevronLeft} />
          </button>
        ) : null}
        {scrollMetrics.canScrollRight ? (
          <button
            type="button"
            className={cx(styles.scrollArrow, styles.scrollArrowRight)}
            onClick={() => scrollByPage('right')}
            aria-label="Scroll right"
          >
            <Icon icon={faChevronRight} />
          </button>
        ) : null}
        {scrollMetrics.canScrollRight ? (
          <div className={styles.fadeRight} aria-hidden="true" />
        ) : null}
        <div
          className={styles.scroller}
          ref={scrollerRef}
          onScroll={() => {
            updateScrollMetrics();
            hideTooltip();
          }}
        >
          <div className={styles.grid} style={gridStyle} role="grid">
            {hasCategories ? (
              <div className={styles.headerRow}>
                <div className={styles.corner} />
                {bands.map((band) => {
                  const info = resolveCategory(band.category, lookups);
                  const name = info?.display_name || band.category;
                  return (
                    <div
                      key={`band-${band.column.id}`}
                      className={cx(
                        styles.band,
                        info?.palette,
                        band.categoryStart && styles.gapColumn
                      )}
                      style={{
                        gridColumn: `${band.start} / span ${band.span}`,
                      }}
                      aria-label={name}
                      onMouseEnter={(event) => onTip(event, { title: name })}
                      onMouseLeave={onHideTip}
                    />
                  );
                })}
              </div>
            ) : null}

            <div className={styles.headerRow}>
              <div className={styles.corner} ref={nameRef} />
              {placed.map(({ column, gridColumn, categoryStart }) => {
                const info = resolveCategory(column.category, lookups);
                const question = column.type === 'question';
                return (
                  <div
                    key={column.id}
                    className={cx(
                      styles.columnHeader,
                      styles.categoryColumn,
                      question ? styles.questionColumn : styles.setColumn,
                      categoryStart && styles.gapColumn,
                      focus && columnCited(column, focus) && styles.focused
                    )}
                    style={{ gridColumn }}
                    aria-label={column.label}
                    onMouseEnter={(event) => onTip(event, columnTip(column))}
                    onMouseLeave={onHideTip}
                  >
                    <span className={cx(styles.columnColor, info?.palette)} />
                    <span className={styles.columnLabel}>{column.label}</span>
                  </div>
                );
              })}
            </div>

            <MemoHeatmapRows
              rows={rows}
              placed={placed}
              heatmap={heatmap}
              pairs={pairs}
              focusPairs={focusPairs}
              open={open}
              lookups={lookups}
              focus={focus}
              onTip={onTip}
              onHideTip={onHideTip}
              onToggleGroup={onToggleGroup}
              onSelectTeam={onSelectTeam}
            />
          </div>
        </div>
      </div>
      {tooltipOpen && tooltipData ? (
        <TooltipInPortal
          left={tooltipLeft}
          top={tooltipTop}
          style={TOOLTIP_STYLE}
          className={styles.tip}
        >
          {tooltipData.who ? (
            <div className={styles.tipWho}>{tooltipData.who}</div>
          ) : null}
          {tooltipData.title ? (
            <div className={styles.tipWhat}>{tooltipData.title}</div>
          ) : null}
          {tooltipData.text ? (
            <div className={styles.tipText}>{tooltipData.text}</div>
          ) : null}
          {tooltipData.score ? (
            <div className={styles.tipScore}>{tooltipData.score}</div>
          ) : null}
        </TooltipInPortal>
      ) : null}
      <Details
        selection={selection}
        reads={reads}
        onClose={() => setSelection(null)}
      />
      <div className={styles.legend}>
        {showScores ? (
          <span>
            Low
            <span
              className={styles.trafficRamp}
              style={{
                background: `linear-gradient(to right, ${toHexColor(
                  scaleColorTraffic(0)
                )}, ${toHexColor(scaleColorTraffic(0.5))}, ${toHexColor(
                  scaleColorTraffic(1)
                )})`,
              }}
            />
            High
          </span>
        ) : (
          <>
            <span>
              <span className={styles.ramp}>
                <span className={cx(styles.swatch, styles.up1)} />
                <span className={cx(styles.swatch, styles.up2)} />
                <span className={cx(styles.swatch, styles.up3)} />
              </span>
              Rose
            </span>
            <span>
              <span className={styles.ramp}>
                <span className={cx(styles.swatch, styles.down1)} />
                <span className={cx(styles.swatch, styles.down2)} />
                <span className={cx(styles.swatch, styles.down3)} />
              </span>
              Fell
            </span>
            <span>
              <span className={styles.swatch} /> Under 5 points
            </span>
          </>
        )}
        <span>
          <span className={cx(styles.swatch, styles.citedSwatch)} /> Cited
        </span>
        {heatmap.rated_by_evaluator ? (
          <span className={styles.note}>Scores are evaluator ratings</span>
        ) : null}
      </div>
    </div>
  );
}
